import React from 'react';
import classNames from 'classnames';
import {
	ClientOpentokHealth,
	NetworkCondition,
	SubscriberHealth,
	worstNetworkCondition,
} from '../../../stores/types/opentok-health';
import { formatAgo } from '../format';

interface Props {
	reports: ClientOpentokHealth[];
	connected: boolean;
	onRefresh: () => void;
}

const DEGRADATION_LABELS: Record<string, string> = {
	local: 'our downlink',
	remote: 'their uplink',
	bothOrUnclear: 'both/unclear',
};

/** Formats bits per second from the SDK's bandwidth estimate. */
const formatBandwidth = (bps?: number): string => {
	if (!bps) return '—';
	if (bps >= 1000000) return `${(bps / 1000000).toFixed(1)}Mbps`;
	return `${Math.round(bps / 1000)}kbps`;
};

const ConditionPill = ({ condition }: { condition?: NetworkCondition }) => (
	<span className={classNames('health-net', `health-net--${condition ?? 'unknown'}`)}>
		{condition ?? 'unknown'}
	</span>
);

/** One subscriber row: what this client is receiving from one remote stream. */
function SubscriberRow({ subscriber }: { subscriber: SubscriberHealth }) {
	const {
		transport, remotePublisherTransport, networkDegradationSource,
		width, height, frameRate, freezeCount, totalFreezesDuration, videoQualityScore,
	} = subscriber;

	// The stream name is `<gr_user>,<guid>`, so the leading segment identifies who.
	const senderId = subscriber.streamName?.split(',')[0];

	return (
		<tr>
			<td>
				<span className='health-mono'>{senderId ?? subscriber.streamId}</span>
			</td>
			<td>
				<ConditionPill condition={transport?.networkCondition} />
				{networkDegradationSource && networkDegradationSource !== 'none' && (
					<span className='health-tag health-tag--warn'>
						{DEGRADATION_LABELS[networkDegradationSource] ?? networkDegradationSource}
					</span>
				)}
				{remotePublisherTransport && (
					<div className='health-subtle'>
						sender: {remotePublisherTransport.networkCondition}
					</div>
				)}
			</td>
			<td className='health-mono'>
				{width && height ? `${width}×${height}` : '—'}
				{!!frameRate && ` @ ${Math.round(frameRate)}fps`}
			</td>
			<td className='health-mono'>
				{videoQualityScore ? videoQualityScore.toFixed(1) : '—'}
			</td>
			<td className='health-mono'>
				{freezeCount ? (
					<span className='health-error-text'>
						{freezeCount}
						{!!totalFreezesDuration && ` (${(totalFreezesDuration / 1000).toFixed(1)}s)`}
					</span>
				) : '0'}
			</td>
		</tr>
	);
}

/**
 * Per-participant OpenTok quality, reported by the clients themselves.
 *
 * This is the only part of the dashboard that is not server-truth, because the
 * Vonage server APIs do not expose per-connection quality at all. Each client
 * answers a request on the meeting socket channel with its own view, and pushes
 * an unprompted report when the SDK reports a degradation.
 */
export default function ClientQualityPanel({ reports, connected, onRefresh }: Props) {
	return (
		<section className='health-panel health-panel--wide'>
			<header className='health-panel__header'>
				<div>
					<h3 className='health-panel__title'>Client-reported quality</h3>
					<span className='health-panel__source'>
						meeting socket · each participant&apos;s own OpenTok stats
					</span>
				</div>
				<div className='health-panel__status'>
					<span className={classNames('health-pill', connected ? 'health-pill--ok' : 'health-pill--empty')}>
						{connected ? `${reports.length} reporting` : 'connecting'}
					</span>
					<button type='button' className='health-link-button' onClick={onRefresh}>
						Ask now
					</button>
				</div>
			</header>

			<div className='health-panel__body'>
				{!connected && <p className='health-panel__note'>Joining the meeting channel…</p>}

				{connected && !reports.length && (
					<p className='health-panel__note'>
						No client answered. Either nobody is in the room, or the participants are on a
						build without health reporting.
					</p>
				)}

				{reports.map((report) => {
					const publisher = report.publishers[0];
					const uplink = publisher?.transport?.networkCondition;
					const worstDownlink = worstNetworkCondition(
						report.subscribers.map((sub) => sub.transport?.networkCondition),
					);
					const cpuStressed = report.cpuPerformanceState === 'serious'
						|| report.cpuPerformanceState === 'critical';

					return (
						<div className='health-client' key={String(report.grUser ?? report.name)}>
							<div className='health-client__header'>
								<strong>{report.name}</strong>
								{report.isProducer && <span className='health-tag'>producer</span>}
								<span className='health-subtle'>{formatAgo(report.collectedAt)}</span>
							</div>

							<dl className='health-facts'>
								<dt>Uplink</dt>
								<dd>
									<ConditionPill condition={uplink} />
									{publisher?.transport?.networkConditionReason
										&& publisher.transport.networkConditionReason !== 'none' && (
										<span className='health-tag health-tag--warn'>
											{publisher.transport.networkConditionReason}
										</span>
									)}
									<span className='health-subtle'>
										est. {formatBandwidth(publisher?.transport?.connectionEstimatedBandwidth)}
									</span>
								</dd>
								<dt>Downlink</dt>
								<dd>
									<ConditionPill condition={worstDownlink} />
									<span className='health-subtle'>
										worst of {report.subscribers.length} subscribed
									</span>
								</dd>
								<dt>CPU</dt>
								<dd>
									<span className={classNames('health-tag', { 'health-tag--warn': cpuStressed })}>
										{report.cpuPerformanceState ?? 'not reported'}
									</span>
								</dd>
								{!!publisher?.layers?.length && (
									<>
										<dt>Sending</dt>
										<dd>
											{publisher.layers.map((layer, index) => (
												// eslint-disable-next-line react/no-array-index-key
												<span className='health-tag' key={index}>
													{layer.width}×{layer.height} @ {Math.round(layer.encodedFrameRate)}fps
													{layer.qualityLimitationReason
														&& layer.qualityLimitationReason !== 'none'
														&& ` · limited by ${layer.qualityLimitationReason}`}
												</span>
											))}
										</dd>
									</>
								)}
							</dl>

							{!report.subscribers.length ? (
								<p className='health-panel__note'>Not subscribed to any streams.</p>
							) : (
								<table className='health-table'>
									<thead>
										<tr>
											<th>Receiving from</th>
											<th>Condition</th>
											<th>Video</th>
											<th>MOS</th>
											<th>Freezes</th>
										</tr>
									</thead>
									<tbody>
										{report.subscribers.map((subscriber) => (
											<SubscriberRow key={subscriber.streamId} subscriber={subscriber} />
										))}
									</tbody>
								</table>
							)}
						</div>
					);
				})}
			</div>
		</section>
	);
}
